import {
  CleanupAll,
  cleanupMethods,
  isPlannerBusy,
  setMode,
  toggleEditableWallViewMode,
} from "src/components/Planner/planner/planner";
import {PlannerGlobals} from "src/components/Planner/planner/planner-globals";
import {getRoomById, isRoomOutside, rebuildWallsCache, uDev} from "src/components/Planner/planner/uDev";
import {uDevDOM} from "src/components/Planner/planner/uDev-dom";
import {ts, ts2} from "src/utils/translate";
import $ from "jquery";
import * as d3 from "d3";
import {
  CMToPx,
  getClosestSegmentToPoint,
  getClosestWallToPoint,
  getWallById,
  getWallOrBorderWallById,
  line_line_intersect2,
  NumToXYPoly,
  P_numtoxy,
  segPt,
  uMath,
  wallClosestPointToPoint,
  XYto2DArr,
  XYtoNumPoly
} from "src/components/Planner/planner/uMath";
import {GlobalsVariables, ROOT_URI} from "src/utils/constants";
import polylabel from "polylabel";
import {wallModHelper} from "src/components/Planner/planner/wallModHelper";
import {ModifyMaterialsAction} from "src/components/Planner/planner/history/room-actions";
import * as Polygonize from "@turf/polygonize";
import {getFrontendURL} from "src/utils/url";
import {
  genericXHRErrorHandler,
  getAuthHeaderSync,
  logDebug,
  toastErrorSingle,
  toastInfo,
  toastInfoSingle,
  toastRemoveClass
} from "src/components/Planner/controller";

/**
 * @typedef {Object} WallSegPoly
 * @property {string} id
 * @property {WallSegMaterial} material
 * @property {[number, number][]} pts detectedType
 */

/**
 * @typedef {Object} WallSegMaterial
 * @property {number} id
 * @property {string} name
 * @property {string} fillColor
 */

/**
 * @typedef {Object} WallSeg
 * @property {string} id
 * @property {number} x1
 * @property {number} y1
 * @property {number} x2
 * @property {number} y2
 */

/**
 * @readonly
 * @enum {number}
 */
export const WALL_SEGMENT_TYPE = {
  unknown: 0,
  outerWall: 1,
  innerWall: 2,
  outerDoor: 3,
  innerDoor: 4,
  window: 5,
  ceiling: 6,
  manual: 7,
}

export class WallSegmentation {
  constructor() {
    /**
     * @type {Object.<string, WallSeg[]>}
     */
    this.splitWallSegments = {};
    this.matsToApply = [];
    this.origiCalculatedPolygons = [];
    this.origiHashOfRooms = null;

    /**
     * @type {WallSegPoly[]}
     */
    this.calculatedPolygons = [];
    this.hashOfCalculatedState = null;
    this.cornerThreshold = 1;
    this.perpendicularThreshold = 0.5;

    this.activeTargetId = 'outerPolygons';
    this.activeMaterialId = 'wall_1';
    this.lastSelectedMaterialIdForTargetId = {};
    this.isMouseDown = false;

    this.setModeBeginSaveData = {};

    this.algoVersion = 1;
    this.loadedDataAlgoVersion = null;
    this.onlyDebugLines = false;
    this.debugMode = GlobalsVariables.env === "dev";

    this.lastSuccessfulSentData = null;

    this.detectedTypes = new Map();
  }

  shouldSaveData(saveData) {
    if (typeof saveData !== "object" || !saveData) { return false; }
    if (typeof this.lastSuccessfulSentData !== "object" || !this.lastSuccessfulSentData) { return true; }
    const sentHash = this.basicHashAlgo(JSON.stringify(this.lastSuccessfulSentData));
    const saveHash = this.basicHashAlgo(JSON.stringify(saveData));
    return sentHash != saveHash;
  }

  roomIdChanged(from, to) {
    const targets = [this.calculatedPolygons, this.origiCalculatedPolygons];
    targets.forEach(target => {
      if (Array.isArray(target)) {
        const ceil = target.find(elem => elem.id == 'ceiling_' + from);
        if (ceil) {
          ceil.id = 'ceiling_' + to;
        }
      }
    });
  }

  setLastSelectedMaterialIdForTargetId(targetId, materialId) {
    this.lastSelectedMaterialIdForTargetId[targetId] = materialId;
  }

  getLastSelectedMaterialIdForTargetId(targetId) {
    if (targetId in this.lastSelectedMaterialIdForTargetId) {
      return this.lastSelectedMaterialIdForTargetId[targetId];
    }
    else {
      return this.getAutoMaterialIdForTargetId(targetId);
    }
  }

  getAutoMaterialIdForTargetId(targetId) {
    const polyGrps = this.getPolyGroups();
    const targetsArray = polyGrps[targetId];

    if (!targetsArray.length) {
      const targetDef = this.getTargets().find(x => x.id == targetId);
      if (targetDef && Array.isArray(targetDef.validMaterialIds)) {
        return targetDef.validMaterialIds[0];
      }
      else {
        return null;
      }
    }

    let matGrps = {};
    targetsArray.forEach(elem => {
      if (!(elem.material.id in matGrps)) {
        matGrps[elem.material.id] = 1;
      }
      else {
        matGrps[elem.material.id]++;
      }
    });
    const materialEntries = Object.entries(matGrps);
    materialEntries.sort((a, b) => b[1] - a[1]); // sort material entries descending by occurrence
    return materialEntries[0][0]; // key of 1st material
  }

  getSaveData() {
    return this.getSaveDataV2();
  }

  getSaveDataV2() {
    try {
      // if cant run because of missing dependency, return empty
      if (!this.canRun()) {
        return this.getEmptySaveDataV2();
      }

      // try calc
      if (!this.calculatedPolygons.length) {
        this.doCalculations();
      }

      // if empty after calc, get empty data
      if (!this.calculatedPolygons.length) {
        return this.getEmptySaveDataV2();
      }

      const mappings = this.getWallSegmentPolyMapping();
      const material_layer = PlannerGlobals.wallSegmentation.calculatedPolygons.map(x => ({ id: x.id, materialId: x.material.id, points: x.pts }));
      const material_splitted_lines = {};

      const allWalls = rebuildWallsCache(false, true, false);

      // get wall segments separately (NO WINDOW, DOOR, CEILING, ETC)
      mappings.validWallSegments
      .filter(x => x.id in mappings.segIdToPoly && mappings.segIdToPoly[x.id].material.id.indexOf('wall_') === 0)
      .forEach(x => {
        const wallId = x.id.split('_')[0];
        const wall = allWalls.find(wall => wall.id == wallId);
        if (wall) {
          const roomKey = 'room_id_' + wall.roomId;
          const splitted_line = {};
          ['x1', 'y1', 'x2', 'y2'].forEach(key => {
            /* splitted_line[key] = +(+x[key]).toFixed(2); */
            splitted_line[key] = this.wallSegRound(x[key]);
          });
          splitted_line.wall_material_id = +mappings.segIdToPoly[x.id].material.id.replace('wall_', '');
          if (!(roomKey in material_splitted_lines)) {
            material_splitted_lines[roomKey] = [];
          }
          material_splitted_lines[roomKey].push(splitted_line);
        }
      });

      const data = {
        version: this.algoVersion,
        material_layer,
        material_splitted_lines,
        hash: this.hashFloorStateV2()
      };

      return data;
    }
    catch (err) {
      logDebug(err);
      return this.getEmptySaveDataV2();
    }
  }

  getEmptySaveData() {
    const hash = -1;
    const polies = [];
    const data = { hash, polies };
    return data;
  }

  getEmptySaveDataV2() {
    return {
      version: this.algoVersion,
      material_splitted_lines: {},
      material_layer: [],
      hash: -1
    }
  }

  isSaveDataV2Valid(saveData) {
    // is saveData a valid object
    if (!(typeof saveData === "object" && saveData)) {
      return false;
    }

    // does it contain every needed key
    if (!('version' in saveData && 'material_splitted_lines' in saveData && 'material_layer' in saveData)) {
      return false;
    }

    // does every key have valid type
    if (!(Array.isArray(saveData.material_layer) && typeof saveData.material_splitted_lines === "object" && saveData.material_splitted_lines)) {
      return false;
    }

    // does every key have content
    if (!(saveData.material_layer.length && Object.keys(saveData.material_splitted_lines).length)) {
      return false
    }


    return true;
  }

  loadFromServer(floorId) {
    return new Promise((res, rej) => {

      // LOCALSTORAGE - DEBUG ONLY
      // const elem = localStorage.getItem('wm_' + floorId) || "{}";
      // logDebug('LOADED FROM LOCALSTORAGE');
      // return res(JSON.parse(elem));

      // -----------

      // SERVER - PRODUCTION
      $.ajax({
        headers: getAuthHeaderSync(),
        method: 'GET',
        url: ROOT_URI + '/floor/get-materials',
        data: {
          floor_id: floorId,
        },
        success: resp => {
          try {
            if (typeof resp === "string") {
              resp = JSON.parse(resp)
            }
            if (typeof resp === "object" && resp) {
              return res(resp);
            }
            return rej();
          }
          catch (err) {
            return rej();
          }
        },
        error: resp => {
          genericXHRErrorHandler(resp, typeof PlannerGlobals.isInQuote !== "undefined" && PlannerGlobals.isInQuote);
          return rej();
        }
      });

    });
  }

  saveToServer(floorId, saveData) {
    return new Promise((res, rej) => {

      const shouldSave = this.shouldSaveData(saveData);
      if (!shouldSave) {
        logDebug('SKIPPING SAVE, SAME NEW STATE AS SERVER');
        return res(true);
      }

      // SERVER - PRODUCTION
      $.ajax({
        headers: getAuthHeaderSync(),
        method: 'POST',
        url: ROOT_URI + '/floor/set-materials',
        data: {
          floor_id: floorId,
          json_data: JSON.stringify(saveData)
        },
        success: resp => {
          this.lastSuccessfulSentData = saveData;
          if (PlannerGlobals.plannerEditableWallViewEnable === true) {
            toggleEditableWallViewMode(true);
          }
          return res(true);
        },
        error: resp => {
          genericXHRErrorHandler(resp);
          //genericXHRErrorHandler(resp, typeof isInQuote !== "undefined" && isInQuote);
          //return rej(false);
        }
      });

    });
  }

  saveState(saveData) {
    if (typeof saveData === "object" && saveData) {
      logDebug(`saving materials with hash ${saveData.hash}`);
      return this.saveToServer(PlannerGlobals.plannerFloorId, saveData);
    }
    else {
      return Promise.reject();
    }
  }

  loadStateWithoutChecks(saveData) {
    try {
      if (typeof saveData === "object" && saveData && 'version' in saveData && Array.isArray(saveData.material_layer)) { // V2
        const mats = this.getMaterials();
        this.polygonizeFloorPlan();

        this.calculatedPolygons = saveData.material_layer.map(elem => ({
          id: elem.id,
          pts: elem.points,
          material: mats[elem.materialId]
        }));

        this.loadedDataAlgoVersion = saveData.version;
      }
    }
    catch (err) {
      logDebug(err);
    }
  }

  /**
   * Used by the planner to load the server-stored values
   * @param {*} saveData
   */
  loadStateFromExistingState(saveData) {
    if (typeof saveData === "object" && saveData && 'version' in saveData && Array.isArray(saveData.material_layer)) { // V2
      const currHash = this.hashFloorStateV2();
      if (this.algoVersion == saveData.version && currHash == saveData.hash) {
        logDebug('hash and version match, keeping save')
        this.loadStateWithoutChecks(saveData);
        const sd = PlannerGlobals.wallSegmentation.getSaveDataV2();
        PlannerGlobals.wallSegmentation.lastSuccessfulSentData = sd;
        return Promise.resolve(true);
      }
      else {
        if (this.algoVersion != saveData.version) {
          logDebug('version mismatch', this.algoVersion, saveData.version);
        }
        if (currHash != saveData.hash) {
          logDebug('hash mismatch', currHash, saveData.hash);
        }
        logDebug('trying to preserve older state');
        return this.recalcPreserveSave(saveData).then(() => {
          const sd = PlannerGlobals.wallSegmentation.getSaveDataV2();
          PlannerGlobals.wallSegmentation.lastSuccessfulSentData = sd;
          return true;
        });
      }
    }
    else {
      logDebug('received empty/invalid material value, generating new save');
      this.polygonizeFloorPlan();
      const newSaveData = this.getSaveDataV2();
      return this.saveToServer(PlannerGlobals.plannerFloorId, newSaveData);
    }
  }

  canRun() {
    try {
      if (typeof PlannerGlobals.LENGTHOF1PX === "undefined" || !(PlannerGlobals.LENGTHOF1PX > 0)) {
        return false;
      }
      if (!uDev.outerBorderExists()) {
        return false;
      }
      if (!PlannerGlobals.rooms.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder)).length) {
        return false;
      }
      return true;
    }
    catch (err) {
      console.error(err);
      return false;
    }
  }

  recalcPreserveSave(preserveData, preventSave = false) {
    return new Promise((res, rej) => {
      if (!this.canRun() || typeof preserveData !== "object" || !preserveData) {
        if (!preventSave) {
          this.saveToServer(PlannerGlobals.plannerFloorId, this.getEmptySaveDataV2());
        }
        return res(false);
      }

      try {
        const currHash = this.hashFloorStateV2();
        const saveHash = preserveData.hash;
        if (currHash == saveHash && saveHash == this.hashOfCalculatedState) {
          logDebug('Skipping poly calc since no state change detected');
        }
        else {
          // recalc
          this.doCalculations();
        }


        // preserve
        if ('material_layer' in preserveData) {
          const mats = this.getMaterials();

          const suitableOldCeilings = [];
          const suitableOldNonCeilings = [];
          preserveData.material_layer.forEach(oldPoly => {
            const mapped = { pts: NumToXYPoly(oldPoly.points), numPts: oldPoly.points, materialId: oldPoly.materialId };
            if (oldPoly.id.indexOf('ceiling_') === 0) {
              suitableOldCeilings.push(mapped);
            }
            else {
              suitableOldNonCeilings.push(mapped);
            }
          });

          const calcCeilings = [];
          const calcNonCeilings = [];
          this.calculatedPolygons.forEach(calcPoly => {
            if (calcPoly.id.indexOf('ceiling_') === 0) {
              calcCeilings.push(calcPoly);
            }
            else {
              calcNonCeilings.push(calcPoly);
            }
          });

          calcCeilings.forEach(calcPoly => {
            const newPolyPts = calcPoly.pts;
            const centerPt = polylabel([newPolyPts]);
            const prevAtThisPlace = suitableOldCeilings.find(oldPoly => {
              const oldPolyPts = oldPoly.numPts;
              return d3.polygonContains(oldPolyPts, centerPt);
            });
            if (prevAtThisPlace) {
              calcPoly.material = mats[prevAtThisPlace.materialId];
            }
          });

          calcNonCeilings.forEach(calcPoly => {
            const newPolyPts = NumToXYPoly(calcPoly.pts);
            const newType = calcPoly.material.id.split('_')[0];
            const prevAtThisPlace = suitableOldNonCeilings.find(oldPoly => {
              const oldType = oldPoly.materialId.split('_')[0];
              const oldPolyPts = oldPoly.pts;
              return newType == oldType && newPolyPts.every(newPt => uMath.pointPolygonDistance(newPt, oldPolyPts) < 0.1);
            });
            if (prevAtThisPlace) {
              calcPoly.material = mats[prevAtThisPlace.materialId];
            }
          });


          if (!preventSave) {
            // save
            const newSaveData = this.getSaveDataV2();
            return this.saveToServer(PlannerGlobals.plannerFloorId, newSaveData).then(() => res(true));
          }
          else {
            return res(true);
          }
        }

        return res(false);
      }
      catch (err) {
        console.error(err);
        return res(false);
      }
    });
  }

  getWallSegmentPolyMapping() {
    const st = +new Date();
    const segIdToPoly = {};
    const validWallSegments = [];

    for (let wallId in this.splitWallSegments) {
      const segArr = this.splitWallSegments[wallId];
      segArr.forEach(segObj => {
        const segment = segObj;
        const segId = segObj.id;
        const segPt1 = segPt(segment, 1);
        const segPt2 = segPt(segment, 2);

        const polyOfSeg = this.calculatedPolygons.find(poly => {
          if (poly.id.indexOf('ceiling_') === 0) { return false; }
          return [segPt1, segPt2].every(segPt => {
            return poly.pts.find(polyPt => {
              return uMath.pointDistance(P_numtoxy(polyPt), segPt) < 0.01;
            });
          });
        });

        if (polyOfSeg) {
          segIdToPoly[segId] = polyOfSeg;
          validWallSegments.push(segObj);
        }
      });
    }

    const et = +new Date();
    return { segIdToPoly, validWallSegments };
  }

  getInnerOuterRoomIds() {
    const isInner = {};
    uDev.roomsExtended.forEach(room => {
      isInner[room.id] = !isRoomOutside(room, PlannerGlobals.outerBorder);
    });
    return isInner;
  }

  getWallTypeForDevs(opts) {
    if (!this.calculatedPolygons.length) {
      this.doCalculations();
    }
    if (typeof opts !== "object" || !opts) {
      opts = {};
    }
    const filterFn = opts.filterFn;
    const maxDist = 'maxDist' in opts ? opts.maxDist : -1;
    const ignoreFar = 'ignoreFar' in opts ? opts.ignoreFar : false;
    const ignoreOutdoors = 'ignoreOutdoors' in opts ? opts.ignoreOutdoors : false;

    const roomInnerMap = this.getInnerOuterRoomIds();
    const mappings = this.getWallSegmentPolyMapping();
    const validWallSegments = mappings.validWallSegments;
    if (!validWallSegments.length) { return {}; }
    const segIdToPoly = mappings.segIdToPoly;

    const devs = typeof filterFn === "function" ? PlannerGlobals.devices.filter(dev => filterFn(dev)) : PlannerGlobals.devices;
    const idToMaterial = {};
    devs.forEach(dev => {
      const isInner = roomInnerMap[dev.roomId];
      if (isInner) {
        const devCenter = uDevDOM.getCenterOfDevice(dev.deviceId);
        const closestSegment = getClosestSegmentToPoint(validWallSegments, devCenter);
        const dist = uMath.pointSegmentDistance_S(devCenter, closestSegment);
        if (dist > maxDist) {
          if (!ignoreFar) {
            idToMaterial[dev.deviceId] = `far_from_wall (FAR FROM WALL)`;
          }
        }
        else {
          const polyOfSegment = segIdToPoly[closestSegment.id];
          const material = polyOfSegment.material;
          idToMaterial[dev.deviceId] = `${material.id} (${material.name})`;
        }
      }
      else {
        if (!ignoreOutdoors) {
          idToMaterial[dev.deviceId] = `outdoors (OUTDOORS)`;
        }
      }
    });
    return idToMaterial;
  }

  /**
   * example function for testing this tool
   */
  getWallTypeForVentilators() {
    if (!this.calculatedPolygons.length) {
      this.doCalculations();
    }

    rebuildWallsCache();
    const mappings = this.getWallSegmentPolyMapping();
    const vents = PlannerGlobals.devices.filter(dev => dev.product_name_id == 148);
    vents.forEach(vent => {
      const center = uDevDOM.getCenterOfDevice(vent.deviceId);
      const closestWall = getClosestWallToPoint(center);
      const wallSegmentsArray = this.splitWallSegments[closestWall.id];
      const closestSegment = getClosestSegmentToPoint(wallSegmentsArray, center);
      const polyOfSegment = mappings.segIdToPoly[closestSegment.id];
      const material = polyOfSegment.material;
    });
  }

  doCalculations() {
    if (!this.canRun()) {
      return false;
    }
    return this.polygonizeFloorPlan();
    //this.getWallSegmentPolyMapping();
  }

  addWallSegStyle() {
    let styleBody = this.getTargets()
    .map(x => `#wallSegments.selected-type-${x.targetType} > .wall-segment-type-${x.targetType}`).join(', ');

    styleBody += ` {
            fill-opacity: 0.85;
            stroke-width: 0.6px;
        } `;

    styleBody += this.getTargets()
    .map(x => `#wallSegments.selected-type-${x.targetType} > .wall-segment-type-${x.targetType}:hover`).join(', ');

    styleBody += ` {
            cursor: pointer;
            filter: drop-shadow(0px 0px 1.5px #222);
        }`;

    $('body').prepend(`<style id="wallSegStyle">${styleBody}</style>`)
  }

  setMode() {
    // check prerequisites
    if (!PlannerGlobals.LENGTHOF1PX) {
      return toastInfoSingle(ts("p_ws_prereq_scale", "Please set the floor's scale before setting the building's materials. <b>(Hotkey: 1)</b>"), false, "wsprereq");
    }
    if (!uDev.outerBorderExists()) {
      return toastInfoSingle(ts("p_ws_prereq_outerwalls", "Please draw the outer walls before setting the building's materials. <b>(Hotkey: 1)</b>"), false, "wsprereq");
    }
    if (!PlannerGlobals.rooms.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder)).length) {
      return toastInfoSingle(ts("p_ws_prereq_room", "Please draw at least one inner room before setting the building's materials. <b>(Hotkey: 2)</b>"), false, "wsprereq");
    }

    // check if planner is busy
    var busyReason = isPlannerBusy();
    if (busyReason) {
      toastInfo(busyReason);
      return false;
    }
    CleanupAll();

    $('#wallSegStyle').remove();
    this.addWallSegStyle();

    // show only needed buttons for this mode
    $('.planner-icon-separator').addClass('hidden');
    $('#planner-buttons .planner-icon-btn').not('#set-wall-types-btn').addClass('hidden');
    $('#confirm-modify-btn, #undo-modify-btn').removeClass('hidden');
    $('#set-wall-types-btn').addClass('pressed');

    this.setModeBeginSaveData = this.getSaveData();

    // calculate polies and draw them
    if (!this.hasCalculatedResults()) {
      this.doCalculations();
    }
    this.redrawCalculatedPolygons();

    // store calculations for cancel
    this.origiHashOfRooms = this.hashOfCalculatedState;
    this.origiCalculatedPolygons = $.extend(true, [], this.calculatedPolygons);

    // setup mousedown event for painting polies
    $('#containerSVG').off('mousedown.wallsg');
    $('#containerSVG').on('mousedown.wallsg', 'polygon.wall-segment', e => {
      if (e.which === 1) {
        this.isMouseDown = true;
        const couldPaint = this.onPolyClick(e.target, 'mousedown');
        if (couldPaint) {
          e.stopImmediatePropagation();
        }
      }
    });

    // setup mouseenter event for painting polies
    $('#containerSVG').off('mouseenter.wallsg');
    $('#containerSVG').on('mouseenter.wallsg', 'polygon.wall-segment', e => {
      if (this.isMouseDown) {
        //e.stopImmediatePropagation();
        const couldPaint = this.onPolyClick(e.target, 'mouseenter');
        if (couldPaint) {
          e.stopImmediatePropagation();
        }
      }
    });

    $(document).off('mousemove.wallsg');
    $(document).on('mousemove.wallsg', e => {
      if (this.isMouseDown) {
        this.toolbarFadeOut();
      }
    });

    $(document).off('mouseup.wallsg');
    $(document).on('mouseup.wallsg', e => {
      if (e.which === 1) {
        this.isMouseDown = false;
        this.toolbarFadeIn();
      }
    });

    this.addMaterialSelector();

    PlannerGlobals.mode = 'wallSegmentation';
    $('#plannerSVG').attr('data-mode', PlannerGlobals.mode);

    if (this.calculatedPolygons.length === 0) {
      setMode.basic();
      toastErrorSingle(ts("p_ws_general_err", "Sorry, we couldn't detect the walls correctly on this plan. Please review your scale, outer walls, and rooms."), false, "wsgenerr");
      return;
    }
  }

  toolbarFadeOut() {
    $('#planner-buttons, #planner-sizers').addClass('wall-segmentation-fade-out');
  }

  toolbarFadeIn() {
    $('#planner-buttons, #planner-sizers').removeClass('wall-segmentation-fade-out');
  }

  selectActiveTarget(id) {
    const activeTarget = this.getTargets().find(x => x.id == id);

    if (activeTarget) {
      $('.wall-target-btn').removeClass('pressed');
      $(`.wall-target-btn[data-targetid="${id}"]`).addClass('pressed');

      if ('validMaterialIds' in activeTarget) {
        $('.wall-material-btn').addClass('hidden');
        activeTarget.validMaterialIds.forEach(validMaterialId => {
          $(`.wall-material-btn[data-materialid="${validMaterialId}"]`).removeClass('hidden');
        });
      }
      else {
        $('.wall-material-btn').removeClass('hidden');
      }

      for (let key in WALL_SEGMENT_TYPE) {
        const type = WALL_SEGMENT_TYPE[key];
        $('#wallSegments').removeClass('selected-type-' + type);
      }

      if ('targetType' in activeTarget) {
        $('#wallSegments').addClass('selected-type-' + activeTarget.targetType);
      }

      this.activeTargetId = activeTarget.id;

      try {
        const autoMat = this.getLastSelectedMaterialIdForTargetId(activeTarget.id);
        this.selectActiveMaterial(autoMat);
      }
      catch (err) {
        logDebug(err);
        this.selectActiveMaterial(activeTarget.validMaterialIds[0]);
      }
    }
  }

  selectActiveMaterial(id) {
    $('.wall-material-btn').removeClass('pressed');
    this.activeMaterialId = id;
    $(`.wall-material-btn[data-materialid="${id}"]`).addClass('pressed');
    this.setLastSelectedMaterialIdForTargetId(this.activeTargetId, id);
  }

  getPolyGroups() {
    /**
     * @type {WallSeg[]}
     */
    const outerSegments = [];
    /**
     * @type {WallSegPoly[]}
     */
    const innerPolygons = [];
    /**
     * @type {WallSegPoly[]}
     */
    const outerPolygons = [];
    /**
     * @type {WallSegPoly[]}
     */
    const windowPolygons = [];
    /**
     * @type {WallSegPoly[]}
     */
    const innerDoorPolygons = [];
    /**
     * @type {WallSegPoly[]}
     */
    const outerDoorPolygons = [];
    /**
     * @type {WallSegPoly[]}
     */
    const ceilingPolygons = [];

    for (let key in this.splitWallSegments) {
      if (key.startsWith("OW_")) {
        this.splitWallSegments[key].forEach(segment => {
          outerSegments.push(segment);
        });
      }
    }

    const windows = wallModHelper.getWallModsByType('window');
    const doors = wallModHelper.getWallModsByType('door');

    this.calculatedPolygons.forEach(poly => {
      if (poly.id.indexOf('ceiling_') === 0) {
        ceilingPolygons.push(poly);
        return;
      }
      const polyPts = poly.pts.map(P_numtoxy);
      const matchingWindow = windows.find(window => {
        return window.points.every(windowPt => {
          return polyPts.some(polyPt => {
            return uMath.pointDistance(windowPt, polyPt) < 2;
          });
        });
      });

      if (matchingWindow) {
        windowPolygons.push(poly);
        return;
      }

      const matchingDoor = doors.find(door => {
        return door.points.every(doorPt => {
          return polyPts.some(polyPt => {
            return uMath.pointDistance(doorPt, polyPt) < 2;
          });
        });
      });

      const matchingOuterSegment = outerSegments.find(seg => {
        const p1 = segPt(seg, 1);
        const p2 = segPt(seg, 2);
        return [p1, p2].some(segmentPt => {
          return poly.pts.find(polyPt => uMath.pointDistance(P_numtoxy(polyPt), segmentPt) < 0.01);
        });
      });

      if (matchingDoor) {
        if (matchingOuterSegment) {
          outerDoorPolygons.push(poly);
        }
        else {
          innerDoorPolygons.push(poly);
        }
        return;
      }

      if (matchingOuterSegment) {
        outerPolygons.push(poly);
      }
      else {
        innerPolygons.push(poly);
      }
    });

    innerPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.innerWall));
    outerPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.outerWall));
    windowPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.window));
    innerDoorPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.innerDoor));
    outerDoorPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.outerDoor));
    ceilingPolygons.forEach(poly => this.detectedTypes.set(poly.id, WALL_SEGMENT_TYPE.ceiling));

    return { innerPolygons, outerPolygons, windowPolygons, innerDoorPolygons, outerDoorPolygons, ceilingPolygons };
  }

  getCeilingTypes() {
    if (!this.calculatedPolygons.length) {
      this.doCalculations();
    }
    const results = {};

    this.calculatedPolygons.filter(poly => poly.id.indexOf('ceiling_') === 0).forEach(poly => {
      const roomId = poly.id.replace('ceiling_', '');
      const materialId = +poly.material.id.replace('ceiling_', '');
      results[roomId] = materialId;
    });

    return results;
  }

  getWallSegmentsWithMaterials() {
    if (!this.calculatedPolygons.length) {
      this.doCalculations();
    }

    const resultArr = [];
    const mapping = this.getWallSegmentPolyMapping().segIdToPoly;

    for (let segId in this.splitWallSegments) {
      const isOuterBorder = segId.indexOf('OW_') === 0;
      const segArr = this.splitWallSegments[segId];
      for (let i = 0; i < segArr.length; i++) {
        const elem = Object.assign({}, segArr[i]);
        const mappingElem = mapping[elem.id];
        if (mappingElem) {
          const materialId = mappingElem.material.id;
          elem.materialId = materialId;
          elem.isOuterBorder = isOuterBorder;
          resultArr.push(elem);
        }
      }
    }

    return resultArr;
  }

  /**
   *
   * @param {string} materialId
   * @param {"innerPolygons"|"outerPolygons"|"windowPolygons"|"innerDoorPolygons"|"outerDoorPolygons"} targetId
   * @param {boolean} noRedraw
   * @param {any[]} polyGroups
   * @returns
   */
  applyMaterialTo(materialId, targetId, noRedraw = false, polyGroups = false) {
    const material = this.getMaterials()[materialId];
    if (!material) { return false; }

    if (!polyGroups) {
      polyGroups = this.getPolyGroups();
    }

    const polies = polyGroups[targetId];
    if (!polies) { logDebug('wrong target supplied'); return; }
    polies.forEach(poly => {
      poly.material = material;
    });

    if (!noRedraw) {
      this.redrawCalculatedPolygons();
    }
  }

  revert() {
    this.hashOfCalculatedState = this.origiHashOfRooms;
    this.calculatedPolygons = $.extend(true, [], this.origiCalculatedPolygons);
    setMode.basic();
  }

  finish() {
    const saveData = this.getSaveData();
    return this.saveState(saveData).finally(() => {
      const action = new ModifyMaterialsAction(this.setModeBeginSaveData, saveData);
      PlannerGlobals.plannerHistoryManager.addHistoryEntry(action);
      setMode.basic();
    });
  }

  addMaterialSelector() {
    $('#planner-buttons-actions').append(`<div id="wall-segment-materials"></div>`);
    const wholeContainer = $('#wall-segment-materials');
    const secondRowCont = $('<div style="position: absolute; top: 40px; gap: 4px; display: flex; width: max-content;">');
    const thirdRowCont = $('<div style="position: absolute; top: 80px; gap: 4px; display: flex; width: max-content;">');
    wholeContainer.append(secondRowCont);
    wholeContainer.append(thirdRowCont);

    // Add target buttons
    this.getTargets().forEach(target => {
      const iconImg = `<img src="${target.icon}" style="width:20px;height:20px;object-fit:contain;border-radius:0px!important;">`;
      const targetBtn = $(`<div class="wall-select-btn wall-target-btn" style="font-weight: 500; font-size: 13px;" data-targetid="${target.id}">${iconImg}${target.text}</div>`);
      targetBtn.on('click', () => {
        this.selectActiveTarget(target.id);
      });
      wholeContainer.append(targetBtn);
    });

    // Add material buttons
    Object.values(this.getMaterials()).forEach(material => {
      const colorDiv = `<div style="width:12px;height:12px;border-radius:50px!important;background-color:${material.fillColor};"></div>`;
      const materialBtn = $(`<div class="wall-select-btn wall-material-btn" data-materialid="${material.id}">${colorDiv}<span style="font-size:12px;">${material.name}</span></div>`);
      materialBtn.on('click', () => {
        this.selectActiveMaterial(material.id);
      });
      secondRowCont.append(materialBtn);
    });

    // Add apply all btn
    const applyAllBtn = $(`<div class="wall-select-btn"><span style="font-weight:500;font-size:14px;">${ts('p_ws_apply_all', 'Apply to all')}</span></div>`);
    applyAllBtn.on('click', () => {
      this.applyMaterialTo(this.activeMaterialId, this.activeTargetId);
    });

    thirdRowCont.append(applyAllBtn);
    applyAllBtn.tooltip({
      title: ts('p_ws_apply_all_tip', 'Apply the material to the selected part of the building'),
      placement: 'right',
      trigger: 'hover',
      container: 'body',
      viewport: 'body'
    });

    this.selectActiveTarget(this.activeTargetId);
    this.selectActiveMaterial(this.activeMaterialId);
  }

  cleanupWallSegmentation() {
    /* container.selectAll('.wall-segment').remove(); */
    $('#wallSegments').remove();
    $('#wall-segment-materials').remove();
    $('#containerSVG').off('mousedown.wallsg');
    $('#containerSVG').off('mouseenter.wallsg');
    $(document).off('mousemove.wallsg');
    $(document).off('mouseup.wallsg');
    $('#wallSegStyle').remove();
    this.toolbarFadeIn();
  }

  hashFloorState() {
    // hashing: rooms, outer border, doors, windows, lengthOf1Px

    // add rooms
    const mappedEntities = PlannerGlobals.rooms
    .filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder))
    .map(room => ({
      id: room.id,
      poly: room.poly.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) }))
    }));
    // add outer border
    mappedEntities.push(PlannerGlobals.outerBorder.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) })));

    // add doors and windows
    wallModHelper.getWallMods()
    .filter(wm => !isRoomOutside(getRoomById(wm.roomId), PlannerGlobals.outerBorder))
    .map(wm => ({
      id: wm.id,
      points: wm.points.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) })),
    })).forEach(wm => mappedEntities.push(wm));

    // add length of 1 px
    mappedEntities.push("L1P_" + Number(PlannerGlobals.LENGTHOF1PX).toFixed(6));

    // join entities to a string
    let entitiesString = mappedEntities.map(room => JSON.stringify(room)).join(';');

    entitiesString += ";version:" + this.algoVersion;
    entitiesString += ";debug:" + this.onlyDebugLines;

    let hash = 0;
    for (let i = 0; i < entitiesString.length; i++) {
      let char = entitiesString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  hashFloorStateV2() {
    if (!this.canRun()) {
      return -1;
    }
    // hashing: rooms, outer border, doors, windows, lengthOf1Px
    // SKIPPING ALGO VERSION!

    // add rooms
    const mappedEntities = PlannerGlobals.rooms
    .filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder))
    .map(room => ({
      id: room.id,
      poly: room.poly.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) }))
    }));
    // add outer border
    mappedEntities.push(PlannerGlobals.outerBorder.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) })));

    // add doors and windows
    wallModHelper.getWallMods()
    .filter(wm => !isRoomOutside(getRoomById(wm.roomId), PlannerGlobals.outerBorder))
    .map(wm => ({
      id: wm.id,
      points: wm.points.map(pt => ({ x: Math.floor(pt.x), y: Math.floor(pt.y) })),
    })).forEach(wm => mappedEntities.push(wm));

    // add length of 1 px
    mappedEntities.push("L1P_" + Number(PlannerGlobals.LENGTHOF1PX).toFixed(6));

    // join entities to a string
    let entitiesString = mappedEntities.map(room => JSON.stringify(room)).join(';');

    let hash = 0;
    for (let i = 0; i < entitiesString.length; i++) {
      let char = entitiesString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  basicHashAlgo(stringInput) {
    let hash = 0;
    for (let i = 0; i < stringInput.length; i++) {
      let char = stringInput.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  getTargets() {
    return [
      {
        id: 'outerPolygons',
        text: ts('p_ws_tar_outer_polygons', 'Outer walls'),
        icon: getFrontendURL() + '/images/wall_32.png',
        validMaterialIds: ['wall_1', 'wall_2', 'wall_3'],
        targetType: WALL_SEGMENT_TYPE.outerWall
      },
      {
        id: 'innerPolygons',
        text: ts('p_ws_tar_inner_polygons', 'Inner walls'),
        icon: getFrontendURL() + '/images/wall_32.png',
        validMaterialIds: ['wall_1', 'wall_2', 'wall_3'],
        targetType: WALL_SEGMENT_TYPE.innerWall
      },
      {
        id: 'outerDoorPolygons',
        text: ts('p_ws_tar_outer_door_polygons', 'Outer doors'),
        icon: getFrontendURL() + '/images/door_32.png',
        validMaterialIds: ['door_1', 'door_2'],
        targetType: WALL_SEGMENT_TYPE.outerDoor
      },
      {
        id: 'innerDoorPolygons',
        text: ts('p_ws_tar_inner_door_polygons', 'Inner doors'),
        icon: getFrontendURL() + '/images/door_32.png',
        validMaterialIds: ['door_1', 'door_2'],
        targetType: WALL_SEGMENT_TYPE.innerDoor
      },
      {
        id: 'windowPolygons',
        text: ts('p_ws_tar_window_polygons', 'Windows'),
        icon: getFrontendURL() + '/images/window_32.png',
        validMaterialIds: ['window_1', 'window_2', 'window_3'],
        targetType: WALL_SEGMENT_TYPE.window
      },
      {
        id: 'ceilingPolygons',
        text: ts('p_ws_tar_ceiling_polygons', 'Ceilings'),
        icon: getFrontendURL() + '/images/ceiling_32.png',
        validMaterialIds: ['ceiling_1', 'ceiling_2', 'ceiling_3', 'ceiling_4'],
        targetType: WALL_SEGMENT_TYPE.ceiling
      }
    ];
  }

  getMaterials() {
    return {
      'wall_1': {
        id: 'wall_1',
        name: ts('p_ws_mat_wall_1', 'Concrete'),
        fillColor: '#848484'
      },
      'wall_2': {
        id: 'wall_2',
        name: ts('p_ws_mat_wall_2', 'Drywall'),
        fillColor: '#fdd866',
      },
      'wall_3': {
        id: 'wall_3',
        name: ts('p_ws_mat_wall_3', 'Brick'),
        fillColor: '#E04734',
      },
      'window_1': {
        id: 'window_1',
        name: ts('p_ws_mat_window_1', 'Glass'),
        fillColor: '#aed0ff',
      },
      'window_2': {
        id: 'window_2',
        name: ts('p_ws_mat_window_2', 'Double pane coated glass'),
        fillColor: '#00419a',
      },
      'window_3': {
        id: 'window_3',
        name: ts('p_ws_mat_window_3', 'Bulletproof glass'),
        fillColor: '#29dcc4',
      },
      'door_1': {
        id: 'door_1',
        name: ts('p_ws_mat_door_1', 'Wood'),
        fillColor: '#795548',
      },
      'door_2': {
        id: 'door_2',
        name: ts('p_ws_mat_door_2', 'Steel'),
        fillColor: '#e0e0e0',
      },
      'ceiling_1': {
        id: 'ceiling_1',
        name: ts('p_ws_mat_ceiling_1', 'Concrete'),
        fillColor: '#848484'
      },
      'ceiling_2': {
        id: 'ceiling_2',
        name: ts('p_ws_mat_ceiling_2', 'Closed false ceiling'),
        fillColor: '#9cc5a1'
      },
      'ceiling_3': {
        id: 'ceiling_3',
        name: ts('p_ws_mat_ceiling_3', 'Exposed concrete'),
        fillColor: '#d2d2d2'
      },
      'ceiling_4': {
        id: 'ceiling_4',
        name: ts('p_ws_mat_ceiling_4', 'Open false ceiling'),
        fillColor: '#aec6cf'
      }
    }
  }

  drawRandomColorLine(segment) {
    const color = `rgb(${Math.random() * 255},${Math.random() * 255},${Math.random() * 255})`;
    PlannerGlobals.container.append('line')
      .attr('class', 'wall-segment')
      .attr('x1', segment.x1)
      .attr('y1', segment.y1)
      .attr('x2', segment.x2)
      .attr('y2', segment.y2)
      .style('stroke-width', 0.5)
      .style('stroke', color);
  }

  /**
   *
   * @param {[number, number][]} poly
   */
  polyIsRoom(poly) {
    poly = NumToXYPoly(poly);
    return PlannerGlobals.rooms.some(room => {
      return poly.every(polyPt => {
        const roomPt = room.poly.find(roomPt => uMath.pointDistance(polyPt, roomPt) < 0.1);
        return !!roomPt;
      });
    });
  }

  /**
   *
   * @param {[number, number][]} poly
   */
  anyRoomCenterInPoly(poly) {
    return PlannerGlobals.rooms.some(room => {
      const roomCenter = d3.polygonCentroid(XYtoNumPoly(room.poly));
      return d3.polygonContains(poly, roomCenter);
    });
  }

  /**
   *
   * @param {[number,number][]]} poly
   */
  anyRoomPolyLabelInPoly(poly) {
    return PlannerGlobals.rooms.some(room => {
      if (!room.labelPlace) {
        room.labelPlace = polylabel([XYto2DArr(room.poly)]);
      }
      return d3.polygonContains(poly, room.labelPlace);
    });
  }

  /**
   *
   * @param {[number, number][]} poly
   * @param {Wall[]} walls
   */
  polyIsNarrowWall(poly, walls) {
    const maxDist = CMToPx(7) / 2;
    /* const center = P_numtoxy(d3.polygonCentroid(poly)); */
    const center = P_numtoxy(polylabel([poly]));
    const tooCloseWall = walls.find(wall => uMath.pointSegmentDistance_S(center, wall) < maxDist);
    return !!tooCloseWall;
  }

  /**
   *
   * @param {Segment[]} segments
   * @param {Segment} newSegment
   */
  findIntersectingSegment(segments, newSegment) {
    const intersects = [];
    for (let i = 0; i < segments.length; i++) {
      const sg = segments[i];
      const intersection = line_line_intersect2(sg, newSegment);
      if (intersection) {
        intersects.push({ idx: i, segment: sg, intersection });
      }
    }
    return intersects;
  }

  getCornersOfRoom(room) {
    const corners = [];
    if (!room || typeof room !== 'object' || !Array.isArray(room.poly) || room.poly.length < 3) { return []; }
    const poly = room.poly;
    for (let i = 0; i < poly.length; i++) {
      const prevPt = poly[i];
      const centerPt = poly[(i + 1) % poly.length];
      const nextPt = poly[(i + 2) % poly.length];
      const seg1 = uMath.segmentFromPoints(prevPt, centerPt);
      const seg2 = uMath.segmentFromPoints(centerPt, nextPt);
      const vec1 = uMath.vectorFromSegment(seg1);
      const vec2 = uMath.vectorFromSegment(seg2);

      const rot = uMath.vectorAngle(vec1, vec2);
      /* console.log(rot) */
      if (rot >= 0.5) {
        corners.push(centerPt);
      }
    }

    return corners;
  }

  hasCalculatedResults() {
    return this.hashOfCalculatedState === this.hashFloorStateV2();
  }

  normalizePoly(poly) {
    if (!Array.isArray(poly)) { return poly; }
    if (uMath.isPolygonClockwise(poly)) {
      const newPoly = Object.assign([], poly);
      return newPoly.reverse();
    }
    else {
      return poly;
    }
  }

  segmentToLineStringCoords(segment) {
    return [[segment.x1, segment.y1], [segment.x2, segment.y2]];
  }

  wallSegRound(num) {
    return +(Math.round(+num * 100) / 100).toFixed(2);
  }

  wallsOfRoom(room) {
    if (!room || !room.poly) {
      return [];
    }
    var walls = [];

    for (var currPt = 0; currPt < room.poly.length; currPt++) {
      var c = room.poly[currPt];
      var n = room.poly[(currPt + 1) % room.poly.length];
      walls.push({
        x1: c.x,
        y1: c.y,
        x2: n.x,
        y2: n.y,
        roomId: room.id,
        id: c.id,
        order_index: c.order_index
      });
    }

    return walls;
  }

  polygonizeFloorPlan() {
    if (!this.canRun()) {
      return false;
    }
    const hashOfCurrRooms = this.hashFloorStateV2();

    const maxOuterWallWidth = CMToPx(100);
    const maxInnerWallWidth = CMToPx(60);

    PlannerGlobals.container.selectAll('.wall-segment').remove();

    let splitterSegments = [];
    const touchedWallNonCorners = {};

    const st = performance.now();
    const nullPt = { x: 0, y: 0 };
    const innerRooms = PlannerGlobals.rooms.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder));
    innerRooms.sort((r1, r2) => uMath.pointDistance(nullPt, r1.poly[0]) - uMath.pointDistance(nullPt, r2.poly[0]));
    const virtualOuterBorderRoom = {
      id: 'OUTER_BORDER',
      poly: PlannerGlobals.outerBorder.map(pt => ({ x: pt.x, y: pt.y, id: "OW_" + (pt.id || pt.order_index) })),
    }
    innerRooms.push(virtualOuterBorderRoom);

    const innerRoomPolies = innerRooms.map(room => this.normalizePoly(room.poly));
    const innerRoomPoliesAndOuterBorder = innerRoomPolies.concat([this.normalizePoly(PlannerGlobals.outerBorder)]);
    innerRooms.forEach(currRoom => {
      const wallsOfCurr = this.wallsOfRoom(currRoom);

      const currRoomCorners = Object.assign([], this.getCornersOfRoom(currRoom) /* currRoom.poly */);
      const currRoomSingleWallMods = wallModHelper.getWallModsOfRoom(currRoom.id).filter(wm => wm.isSingle);
      currRoomSingleWallMods.sort((a, b) => uMath.pointDistance(nullPt, a.points[0]) - uMath.pointDistance(nullPt, b.points[0]));
      currRoomSingleWallMods.forEach(wm => {
        wm.points.forEach(wmp => {
          if (!uMath.isPointOnRoomCorner(currRoom, wmp)) {
            const wall = wallsOfCurr.find(wall => uMath.pointSegmentDistance_S(wmp, wall) < 0.1);
            this.splitWallAtPoint(wall, touchedWallNonCorners, wmp);
            currRoomCorners.push(wmp);
          }
        });
      });

      const otherRooms = innerRooms.filter(otherRoom => otherRoom.id !== currRoom.id);
      otherRooms.forEach(otherRoom => {
        const targetIsOuterWall = otherRoom.id === 'OUTER_BORDER';
        const ternaryRooms = otherRooms.filter(tRoom => tRoom.id !== otherRoom.id);
        const ternaryRoomCorners = ternaryRooms.map(room => room.poly).reduce((p, c) => p.concat(c), []);

        const wallsOfOther = this.wallsOfRoom(otherRoom);

        currRoomCorners.forEach(currRoomCorner => {
          const currRoomOtherCorners = currRoom.poly.filter(pt => pt.id !== currRoomCorner.id);
          wallsOfOther.forEach(wallOfOther => {
            const closestPt = wallClosestPointToPoint(currRoomCorner, wallOfOther);
            const cornerWallDist = uMath.pointDistance(currRoomCorner, closestPt);

            if (targetIsOuterWall && cornerWallDist > maxOuterWallWidth) { return; }
            if (!targetIsOuterWall && cornerWallDist > maxInnerWallWidth) { return; }
            if (cornerWallDist > 1 && uMath.isPointOnAnySegment(closestPt, wallsOfCurr, 1)) { return; }
            if (cornerWallDist > 1 && uMath.isPointOnAnySegment(currRoomCorner, wallsOfOther, 1)) { return; }

            let invalidatorCorners = currRoomOtherCorners.concat(ternaryRoomCorners);
            const cornerOfClosestPt = otherRoom.poly.find(pt => uMath.pointDistance(pt, closestPt) < this.cornerThreshold);

            if (!(wallOfOther.id in touchedWallNonCorners)) {
              touchedWallNonCorners[wallOfOther.id] = [];
            }

            let isCorner = true;
            if (cornerOfClosestPt) {
              invalidatorCorners = invalidatorCorners.concat(otherRoom.poly.filter(pt => pt.id !== cornerOfClosestPt.id));
            }
            else {
              invalidatorCorners = invalidatorCorners.concat(otherRoom.poly);
              isCorner = false;
            }

            const fullSegment = uMath.segmentFromPoints(currRoomCorner, closestPt);
            const pt1 = uMath.lerpPt(currRoomCorner, closestPt, 0.001);
            const pt2 = uMath.lerpPt(currRoomCorner, closestPt, 0.999);
            const segment = uMath.segmentFromPoints(pt1, pt2);

            const linesArePerpendicular = uMath.areSegmentsPerpendicular(wallOfOther, segment, this.perpendicularThreshold);
            const notIntersectingAnything = !uMath.segmentIntersectsAnyPolygon(segment, innerRoomPoliesAndOuterBorder);
            const noCornerOnLine = !uMath.isAnyPointOnSegment(invalidatorCorners, segment, this.cornerThreshold);

            if (linesArePerpendicular && notIntersectingAnything && noCornerOnLine) {
              if (isCorner) {
                fullSegment.x2 = cornerOfClosestPt.x;
                fullSegment.y2 = cornerOfClosestPt.y;
              }

              // Existing segment check start
              const currP1 = segPt(fullSegment, 1);
              const currP2 = segPt(fullSegment, 2);
              const alreadyExistingSegment = splitterSegments.find(x => {
                const xp1 = segPt(x, 1);
                const xp2 = segPt(x, 2);
                return (uMath.pointDistance(currP1, xp1) < 0.01 && uMath.pointDistance(currP2, xp2) < 0.01 ||
                    uMath.pointDistance(currP1, xp2) < 0.01 && uMath.pointDistance(currP2, xp1) < 0.01)
              });
              if (alreadyExistingSegment) { return; }
              // Existing segment check end

              if (!isCorner) {
                touchedWallNonCorners[wallOfOther.id].push(closestPt);
              }
              splitterSegments.push(fullSegment);
            }
          });
        });
      });
    });

    /**
     * @type {WallModConnection[]}
     */
    const doubleWallModConns = $.extend(true, [], PlannerGlobals.wallModConnections).filter(wmc => wmc.rooms.length === 2);
    doubleWallModConns.sort((w1, w2) => uMath.pointDistance(nullPt, w1.rooms[0].points[0]) - uMath.pointDistance(nullPt, w2.rooms[0].points[0]));
    doubleWallModConns.forEach(conn => {
      const wallModPair = conn.rooms;
      /* const isSingle = wallModPair.length === 1;
      if (!isSingle) { */
      const pair1 = wallModPair[0];
      const pair2 = wallModPair[1];
      const p1Room = getRoomById(pair1.roomId);
      const p2Room = getRoomById(pair2.roomId);
      [pair1, pair2].forEach(wm => {
        this.tryAddWallModPointsToNonCorners(wm, touchedWallNonCorners);
      });

      for (let i = 0; i <= 1; i++) {
        if (!uMath.isPointOnRoomCorner(p1Room, pair1.points[i]) && !uMath.isPointOnRoomCorner(p2Room, pair2.points[i])) {
          const seg1 = uMath.segmentFromPoints(pair1.points[i], pair2.points[i]);
          splitterSegments.push(seg1);
        }
      }
      /* } */
    })

    for (let i = 0; i < splitterSegments.length - 1; i++) {
      for (let j = i + 1; j < splitterSegments.length; j++) {
        const s1 = splitterSegments[i];
        const s2 = splitterSegments[j];
        const intersect = line_line_intersect2(s1, s2);
        if (intersect) {
          [s1, s2].forEach(s => {
            s.intersecting = true;
            if (!('midPts' in s)) { s.midPts = []; }
            s.midPts.push(intersect);
          })
        }
      }
    }
    const nonIntersectingSegments = [];
    const intersectingSegments = [];

    for (let i = 0; i < splitterSegments.length; i++) {
      const currSegment = splitterSegments[i]
      if (currSegment.intersecting) {
        intersectingSegments.push(currSegment);
      }
      else {
        nonIntersectingSegments.push(currSegment);
      }
    }

    splitterSegments = nonIntersectingSegments;

    intersectingSegments.forEach(s => {
      const segmentStartPt = segPt(s, 1);
      const segmentEndPt = segPt(s, 2);
      s.midPts = s.midPts.filter(midPt => uMath.pointDistance(midPt, segmentStartPt) > 0.05 && uMath.pointDistance(midPt, segmentEndPt) > 0.05)

      s.midPts.sort((a, b) => uMath.pointDistance(segmentStartPt, a) - uMath.pointDistance(segmentStartPt, b));

      s.midPts.unshift(segmentStartPt);
      s.midPts.push(segmentEndPt);


      for (let i = 1; i < s.midPts.length; i++) {
        const pt1 = s.midPts[i - 1];
        const pt2 = s.midPts[i];
        const seg = uMath.segmentFromPoints(pt1, pt2);

        /* if (uMath.segmentLength(seg) < 1) {
            console.warn('segment too short in 3', seg);
        } */

        splitterSegments.push(seg);
      }
    });

    const et = performance.now();

    const geoJsonLineStrings = this.recalcSplitWallSegments(innerRooms, touchedWallNonCorners);

    splitterSegments.forEach(segment => {
      /* if (uMath.segmentLength(segment) < 1) {
          console.warn('segment too short in 2', segment);
      } */
      geoJsonLineStrings.push({
        type: "LineString",
        coordinates: this.segmentToLineStringCoords(segment),
        src: "__splittersegments"
      });
      /* this.onlyDebugLines && this.drawRandomColorLine(segment); */
    });


    geoJsonLineStrings.forEach(linestring => {
      linestring.coordinates.forEach(coord => {
        coord[0] = this.wallSegRound(coord[0]);
        coord[1] = this.wallSegRound(coord[1]);
        /* coord[0] = +(+coord[0]).toFixed(2);
        coord[1] = +(+coord[1]).toFixed(2); */
        /* coord[0] = Math.round(coord[0] * 100) / 100;
        coord[1] = Math.round(coord[1] * 100) / 100; */
      });
    });


    const pairtestst = performance.now();
    for (let i = 0; i < geoJsonLineStrings.length - 1; i++) {
      for (let j = i + 1; j < geoJsonLineStrings.length; j++) {
        const ls1 = geoJsonLineStrings[i];
        const ls2 = geoJsonLineStrings[j];
        for (let k = 0; k <= 1; k++) {
          for (let l = 0; l <= 1; l++) {
            if (this.pointsSimilarButNotEqual(ls1.coordinates[k], ls2.coordinates[l])) {
              console.debug('found similar coords!', ls1.coordinates[k], ls2.coordinates[l]);
              ls2.coordinates[l][0] = ls1.coordinates[k][0];
              ls2.coordinates[l][1] = ls1.coordinates[k][1];
            }
          }
        }

      }
    }

    if (this.onlyDebugLines) {
      geoJsonLineStrings.forEach(linestring => {
        const p1 = linestring.coordinates[0];
        const p2 = linestring.coordinates[1];
        const seg = { x1: p1[0], y1: p1[1], x2: p2[0], y2: p2[1] };
        this.drawRandomColorLine(seg);
      });

      return;
    }

    try {
      const validLineStrings = geoJsonLineStrings.filter(ls =>
        Array.isArray(ls.coordinates) &&
        ls.coordinates.length >= 2 &&
        !(
          ls.coordinates[0][0] === ls.coordinates[1][0] &&
          ls.coordinates[0][1] === ls.coordinates[1][1]
        )
      );
      const wallSegments = Polygonize.default({
        type: "GeometryCollection",
        geometries: validLineStrings
      });

      /* const currDate = +new Date(); */
      let currId = 1;

      if (typeof wallSegments === "object" && wallSegments && Array.isArray(wallSegments.features)) {
        const innerRoomWalls = PlannerGlobals.rooms.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder)).map(room => this.wallsOfRoom(room)).reduce((p, c) => p.concat(c), []);
        const materials = this.getMaterials();
        this.calculatedPolygons = [];
        wallSegments.features.forEach(feature => {
          const geometry = feature.geometry;
          if (geometry.type === "Polygon") {
            const pts = geometry.coordinates[0];
            if (!pts.length) { return; }

            if (this.polyIsRoom(pts)) { return; }
            /* if (this.anyRoomCenterInPoly(pts)) { return; } */
            if (this.anyRoomPolyLabelInPoly(pts)) { return; }
            if (this.polyIsNarrowWall(pts, innerRoomWalls)) { return; }

            const id = /* currDate + */ "polywall_" + currId++;
            const material = materials.door_1;

            this.calculatedPolygons.push({
              id,
              material,
              pts,
            });
          }
        });

        PlannerGlobals.rooms.filter(room => !isRoomOutside(room, PlannerGlobals.outerBorder)).forEach(room => {
          this.calculatedPolygons.push({
            id: 'ceiling_' + room.id,
            material: materials.ceiling_1,
            pts: room.poly.map(pt => [pt.x, pt.y])
          });
        });

        this.hashOfCalculatedState = hashOfCurrRooms;

        const polyGroups = this.getPolyGroups();

        this.applyMaterialTo('wall_' + PlannerGlobals.CURRENT_MATERIALS.INNER_WALLS.id, "innerPolygons", true, polyGroups);
        this.applyMaterialTo('wall_' + PlannerGlobals.CURRENT_MATERIALS.OUTER_WALLS.id, "outerPolygons", true, polyGroups);
        this.applyMaterialTo('window_' + PlannerGlobals.CURRENT_MATERIALS.WINDOWS.id, "windowPolygons", true, polyGroups);
        this.applyMaterialTo('door_' + PlannerGlobals.CURRENT_MATERIALS.INTERNAL_DOORS.id, "innerDoorPolygons", true, polyGroups);
        this.applyMaterialTo('door_' + PlannerGlobals.CURRENT_MATERIALS.FRONT_DOORS.id, "outerDoorPolygons", true, polyGroups);
        this.applyMaterialTo('ceiling_' + PlannerGlobals.CURRENT_MATERIALS.CEILING.id, "ceilingPolygons", true, polyGroups);

        this.loadedDataAlgoVersion = this.algoVersion;
      }
    }
    catch (err) {
      console.error(err);
      this.calculatedPolygons = [];
    }
  }

  /**
   *
   * @param {[number, number]} p1
   * @param {[number, number]} p2
   * @param {number} similarity
   */
  pointsSimilarButNotEqual(p1, p2, similarity = 0.03) {
    if (p1[0] == p2[0] && p1[1] == p2[1]) { return false; }
    /* const xSimilar = p1[0] <= p2[0] + similarity && p1[0] >= p2[0] - similarity;
    const ySimilar = p1[1] <= p2[1] + similarity && p1[1] >= p2[1] - similarity;
    return xSimilar && ySimilar; */

    return p1[0] <= p2[0] + similarity / 2 && p1[0] >= p2[0] - similarity / 2 &&
        p1[1] <= p2[1] + similarity / 2 && p1[1] >= p2[1] - similarity / 2;
  }

  recalcSplitWallSegments(innerRooms, touchedWallNonCorners) {
    const geoJsonLineStrings = [];
    this.splitWallSegments = {};

    innerRooms.forEach(room => {
      const walls = this.wallsOfRoom(room);

      walls.forEach(wall => {
        this.splitWallSegments[wall.id] = [];
        if (wall.id in touchedWallNonCorners) {
          const wallStartPt = { x: wall.x1, y: wall.y1 };
          const wallEndPt = { x: wall.x2, y: wall.y2 };
          const allSegPts = [wallStartPt];

          const pts = Object.assign([], touchedWallNonCorners[wall.id]);
          pts.sort((pt1, pt2) => {
            const dist1 = uMath.pointDistance(wallStartPt, pt1);
            const dist2 = uMath.pointDistance(wallStartPt, pt2);
            return dist1 < dist2 ? -1 : 1;
          });
          pts.forEach(pt => allSegPts.push(pt));

          allSegPts.push(wallEndPt);

          for (let i = 1; i < allSegPts.length; i++) {
            const pt1 = allSegPts[i - 1];
            const pt2 = allSegPts[i];
            const segment = uMath.segmentFromPoints(pt1, pt2);

            this.splitWallSegments[wall.id].push({ id: wall.id + "_" + i, x1: segment.x1, y1: segment.y1, x2: segment.x2, y2: segment.y2 });
            /* if (uMath.segmentLength(segment) < 1) {
                console.warn('segment too short in 0', segment);
            } */
            geoJsonLineStrings.push({
              type: "LineString",
              coordinates: this.segmentToLineStringCoords(segment)
            });
            /* this.onlyDebugLines && this.drawRandomColorLine(segment); */
          }
        }
        else {
          this.splitWallSegments[wall.id].push({ id: wall.id + "_" + 1, x1: wall.x1, y1: wall.y1, x2: wall.x2, y2: wall.y2 });
          /* this.onlyDebugLines && this.drawRandomColorLine(wall) */
          /* if (uMath.segmentLength(wall) < 1) {
              console.warn('segment too short in 1', wall);
          } */
          geoJsonLineStrings.push({
            type: "LineString",
            coordinates: this.segmentToLineStringCoords(wall)
          });
        }

      });
    });
    geoJsonLineStrings.forEach(x => x.__src = "splitwallseg");
    return geoJsonLineStrings;
  }

  splitWallAtPoint(wall, touchedWallNonCorners, wmp) {
    if (wall) {
      if (!(wall.id in touchedWallNonCorners)) {
        touchedWallNonCorners[wall.id] = [];
      }
      touchedWallNonCorners[wall.id].push(wmp);
    }
  }

  tryAddPointToWallNonCorners(wallId, point, touchedWallNonCorners) {
    const wall = getWallOrBorderWallById(wallId);
    if (wall) {
      const wallPt1 = segPt(wall, 1);
      const wallPt2 = segPt(wall, 2);
      const cornerPt = [wallPt1, wallPt2].find(pt => uMath.pointDistance(point, pt) < 1);

      if (cornerPt) {
        return cornerPt;
      }
      else {
        if (!(wallId in touchedWallNonCorners)) {
          touchedWallNonCorners[wallId] = [];
        }
        const existingClosePt = touchedWallNonCorners[wallId].find(pt => uMath.pointDistance(point, pt) < 0.1);
        if (existingClosePt) {
          return existingClosePt;
        }
        else {
          touchedWallNonCorners[wallId].push(point);
          return point;
        }
      }
    }
  }

  tryAddWallModPointsToNonCorners(wm, touchedWallNonCorners) {
    wm.points.forEach((wmpt, idx, arr) => {
      const wall = wm.wallIds.map(getWallById).find(wall => uMath.pointSegmentDistance_S(wmpt, wall) < 0.1);
      if (wall) {
        const wallPt1 = segPt(wall, 1);
        const wallPt2 = segPt(wall, 2);
        const cornerPt = [wallPt1, wallPt2].find(pt => uMath.pointDistance(wmpt, pt) < 1);
        if (cornerPt) {
          /* arr[idx] = cornerPt; */
          return false;
        }
        else {
          if (!(wall.id in touchedWallNonCorners)) {
            touchedWallNonCorners[wall.id] = [];
          }

          const existingClosePt = touchedWallNonCorners[wall.id].find(pt => uMath.pointDistance(wmpt, pt) < 0.1);
          if (existingClosePt) {
            /* arr[idx] = existingClosePt; */
            return false;
          }
          else {
            touchedWallNonCorners[wall.id].push(wmpt);
            return true;
          }
        }
      }
    });
  }

  redrawCalculatedPolygons() {
    const className = $('#wallSegments').attr('class');
    PlannerGlobals.container.select('#wallSegments, #wallSegmentsShadow').remove();

    const polyContainer = PlannerGlobals.container.append('g').attr('id', 'wallSegments')
    /* const polyContainerShadow = polyContainer.append('g').attr('id', 'wallSegmentsShadow') */

    if (className) {
      polyContainer.attr('class', className);
    }

    this.calculatedPolygons.forEach(polygon => {
      const type = this.detectedTypes.get(polygon.id) || WALL_SEGMENT_TYPE.unknown
      const classType = 'wall-segment-type-' + type;
      const pts = polygon.pts.map(pt => pt[0] + "," + pt[1]).join(' ')
      polyContainer.append('polygon')
        .attr('class', 'wall-segment ' + classType)
        .attr('points', pts)
        .attr('data-id', polygon.id)
        .attr('data-type', type)
        .style('fill', polygon.material.fillColor);
    })
  }

  getCurrTarget() {
    return this.getTargets().find(x => x.id === this.activeTargetId);
  }

  /**
   *
   * @param {SVGPolygonElement} polyElem
   * @param {"mousedown"|"mouseenter"} eventType
   */
  onPolyClick(polyElem, eventType) {
    const id = polyElem.dataset.id;
    const type = polyElem.dataset.type;
    if (this.getCurrTarget().targetType == type) {
      const savedSegment = this.setSegmentMaterial(id, this.activeMaterialId);
      if (savedSegment) {
        toastRemoveClass('wallSegPolyClickErr');
        polyElem.style.fill = savedSegment.material.fillColor;
        return true;
      }
    }
    else if (eventType === "mousedown") {
      const goodTarget = this.getTargets().find(x => x.targetType == type);
      const msgPt1 = ts2('p_ws_paint_help', 'To paint this part, please use the corresponding tool ({tool_name}).', { tool_name: goodTarget.text });
      toastInfoSingle(msgPt1, undefined, 'wallSegPolyClickErr');
      return false;
    }
    return false;
  }

  getSegmentById(segmentId) {
    return this.calculatedPolygons.find(segment => segment.id == segmentId);
  }

  setSegmentMaterial(segmentId, materialId) {
    const segment = this.getSegmentById(segmentId);
    if (!segment) { return false; }
    const material = this.getMaterials()[materialId];
    if (!material) { return false; }
    segment.material = material;
    return segment;
  }

  init() {
    setMode['wallSegmentation'] = () => {
      if (PlannerGlobals.mode != 'wallSegmentation') {
        this.setMode();
      }
    }
    cleanupMethods.push(() => {
      // if (mode == 'wallSegmentation') {
      this.cleanupWallSegmentation();
      // }
    });
  }
}
